import {Row} from '@tanstack/react-table';
import {
  defaultRangeExtractor,
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from '@tanstack/react-virtual';
import {RefObject, useCallback, useLayoutEffect, useRef, useState} from 'react';

import {LOAD_TRIGGER_HEIGHT_IN_REM} from './LoadTrigger';
import {ETableRow} from './types';

/**
 * Number of rows above which the table body is virtualized. Smaller tables
 * render every row, which keeps the automatic column sizing stable and the
 * DOM simple.
 */
export const VIRTUALIZATION_THRESHOLD = 50;

/** Fallback for the `--table-row-height` CSS property. */
const FALLBACK_ROW_HEIGHT = 42;

/** Number of rows that are rendered beyond the visible range. */
const OVERSCAN = 10;

export type ETableVirtualizer = {
  /** Whether the table body is currently virtualized. */
  isVirtualized: boolean;
  /** The virtualized window of rows (empty when not virtualized). */
  virtualItems: VirtualItem[];
  /** Height (in px) of the spacer row rendered before the virtual window. */
  topSpacerHeight: number;
  /** Height (in px) of the spacer row rendered after the virtual window. */
  bottomSpacerHeight: number;
  /** Ref callback used to measure the actual height of a rendered row. */
  measureElement: (element: HTMLTableRowElement | null) => void;
  /**
   * Scrolls to a row that is not rendered because it is outside the virtual
   * window. No-op when the table is not virtualized or the row is unknown.
   * The callback identity is stable.
   */
  scrollToRow: (rowId: string, align?: 'auto' | 'center') => void;
  /**
   * Pins a row (by id) so it stays mounted regardless of the scroll
   * position, e.g. while it is being dragged. Pass `null` to unpin.
   * The callback identity is stable.
   */
  setPinnedRow: (rowId: string | null) => void;
};

/**
 * Virtualizes the ETable body: only the rows within (and around) the visible
 * scroll range are rendered, while spacer rows keep the scroll geometry of
 * the full data window intact.
 */
export function useETableVirtualizer({
  rows,
  wrapperRef,
  tableRef,
  hasMoreBefore,
  disabled,
}: {
  rows: Row<ETableRow>[];
  /** The scrollable element that wraps the table. */
  wrapperRef: RefObject<HTMLDivElement | null>;
  tableRef: RefObject<HTMLTableElement | null>;
  /** Whether the top load trigger row is rendered before the data rows. */
  hasMoreBefore?: boolean;
  disabled?: boolean;
}): ETableVirtualizer {
  // A table nested inside another table (e.g. in an expansion row) has no
  // bounded scroll wrapper of its own — the wrapper just grows with the
  // content, and virtualizing against it would mount ever more rows as the
  // wrapper reports its grown size. Detected through the DOM (instead of a
  // React context) so tables rendered into portals are not affected.
  const [isNested, setIsNested] = useState(false);
  useLayoutEffect(() => {
    // detected once; a table is never moved into another table later on
    setIsNested(Boolean(wrapperRef.current?.closest('table')));
  }, [wrapperRef]);

  /**
   * Whether the scroll wrapper limits its own height. One that grows with its
   * content instead (e.g. `height: 100%` resolving to `auto` inside a container
   * without a definite height) reports the whole content as its viewport, so
   * virtualizing against it would mount every row anyway while the fixed column
   * layout would still apply. Measured while every row is still mounted: a
   * wrapper that limits its height then has more content than viewport.
   */
  const [isWrapperScrollable, setIsWrapperScrollable] = useState<boolean>();
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (
      isWrapperScrollable !== undefined ||
      !wrapper ||
      rows.length <= VIRTUALIZATION_THRESHOLD ||
      // not laid out yet, e.g. still on a hidden tab
      wrapper.clientHeight === 0
    ) {
      return;
    }
    setIsWrapperScrollable(wrapper.scrollHeight > wrapper.clientHeight);
  }, [rows.length, isWrapperScrollable, wrapperRef]);

  const isVirtualized =
    !disabled &&
    !isNested &&
    isWrapperScrollable === true &&
    rows.length > VIRTUALIZATION_THRESHOLD;

  const estimatedRowHeight = useRef<number>(undefined);

  /**
   * Height a row is estimated with as long as it is not measured, taken from
   * the CSS custom property. Read when the virtualizer first asks for it and
   * kept from then on, so it costs one getComputedStyle instead of one per row
   * and render. Rows with breaking content or expanded sub rows correct
   * themselves through the dynamic row measurement.
   */
  const getEstimatedRowHeight = useCallback((): number => {
    if (estimatedRowHeight.current !== undefined) {
      return estimatedRowHeight.current;
    }
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      // not mounted yet; the next call reads the property
      return FALLBACK_ROW_HEIGHT;
    }
    const cssRowHeight = parseFloat(
      getComputedStyle(wrapper).getPropertyValue('--table-row-height')
    );
    estimatedRowHeight.current =
      Number.isFinite(cssRowHeight) && cssRowHeight > 0
        ? cssRowHeight
        : FALLBACK_ROW_HEIGHT;
    return estimatedRowHeight.current;
  }, [wrapperRef]);

  // refs keep the virtualizer callbacks below stable while reading the
  // latest values
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const isVirtualizedRef = useRef(isVirtualized);
  isVirtualizedRef.current = isVirtualized;
  /** Row that must stay mounted regardless of the scroll position. */
  const pinnedRowIdRef = useRef<string | null>(null);

  /**
   * Offset (in px) of the first data row from the top of the scrollable
   * content, i.e. the table header plus the top load trigger row (if any).
   */
  const headerHeight =
    tableRef.current?.querySelector('thead')?.offsetHeight ?? 0;
  const scrollMargin =
    headerHeight +
    (hasMoreBefore ? LOAD_TRIGGER_HEIGHT_IN_REM * getRemInPx() : 0);

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    enabled: isVirtualized,
    getScrollElement: () => wrapperRef.current,
    // when `enabled` flips on (a loaded page crossed the threshold), the
    // virtualizer boots from its initial state — seed it with the live
    // scroll position and viewport size. The default offset 0 would jump to
    // the first row; the default zero-height rect would make the first
    // virtualized render an empty window, collapsing the table body to its
    // spacers, so the browser would clamp the scroll position to the
    // shrunken content
    initialOffset: () => wrapperRef.current?.scrollTop ?? 0,
    initialRect: {
      width: wrapperRef.current?.offsetWidth ?? 0,
      height: wrapperRef.current?.offsetHeight ?? 0,
    },
    estimateSize: getEstimatedRowHeight,
    overscan: OVERSCAN,
    // stable keys make the measured row heights survive prepended pages
    getItemKey: (index) => rows[index]?.id ?? index,
    scrollMargin,
    // keep the pinned (i.e. currently dragged) row mounted by extending the
    // rendered range contiguously up or down to its index; unmounting the
    // dragged row would abort the browser's drag operation
    rangeExtractor: (range) => {
      const pinnedRowId = pinnedRowIdRef.current;
      const pinnedIndex =
        pinnedRowId === null
          ? -1
          : rowsRef.current.findIndex((row) => row.id === pinnedRowId);
      if (pinnedIndex === -1) {
        return defaultRangeExtractor(range);
      }
      return defaultRangeExtractor({
        ...range,
        startIndex: Math.min(range.startIndex, pinnedIndex),
        endIndex: Math.max(range.endIndex, pinnedIndex),
      });
    },
    // a row's virtual item includes its expansion sub rows, which are
    // rendered as sibling <tr>s tagged with the parent's id
    measureElement: (element) => {
      let height = element.offsetHeight;
      const rowId = element.dataset.id;
      for (
        let sibling = element.nextElementSibling;
        sibling instanceof HTMLElement && sibling.dataset.parentId === rowId;
        sibling = sibling.nextElementSibling
      ) {
        height += sibling.offsetHeight;
      }
      return height;
    },
  });

  // when a row above the viewport is measured to a different height than
  // estimated (e.g. freshly prepended rows), keep the visible rows in place
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (
    item,
    _delta,
    instance
  ) => item.start < (instance.scrollOffset ?? 0);

  usePrependScrollCompensation({
    rows,
    wrapperRef,
    virtualizer,
    hasMoreBefore,
    enabled: isVirtualized,
  });

  const setPinnedRow = useCallback((rowId: string | null) => {
    pinnedRowIdRef.current = rowId;
  }, []);

  const scrollToRow = useCallback(
    (rowId: string, align: 'auto' | 'center' = 'auto') => {
      if (!isVirtualizedRef.current) {
        return;
      }
      const index = rowsRef.current.findIndex((row) => row.id === rowId);
      if (index >= 0) {
        virtualizer.scrollToIndex(index, {align});
      }
    },
    [virtualizer]
  );

  const virtualItems = isVirtualized ? virtualizer.getVirtualItems() : [];
  const [firstItem] = virtualItems;
  const lastItem = virtualItems[virtualItems.length - 1];

  return {
    isVirtualized,
    virtualItems,
    // Item positions include the scroll margin, the spacers must not.
    // Note that getTotalSize() *excludes* the scroll margin (virtual-core
    // returns `end - scrollMargin`), while item.start/item.end include it.
    topSpacerHeight: firstItem
      ? Math.max(firstItem.start - scrollMargin, 0)
      : 0,
    bottomSpacerHeight: lastItem
      ? Math.max(virtualizer.getTotalSize() + scrollMargin - lastItem.end, 0)
      : 0,
    measureElement: virtualizer.measureElement,
    scrollToRow,
    setPinnedRow,
  };
}

/**
 * Keeps the visible rows in place when the data window changes at its start.
 * Rows prepended by the top load trigger shift the scroll down by their
 * heights; rows trimmed from the start shift it back up; the top load trigger
 * row disappearing (`hasMoreBefore` exhausted) shifts it up by the trigger
 * height. The non-virtualized table gets all this from the LoadTrigger's DOM
 * scroll anchoring, but with a virtualizer it must happen synchronously,
 * before the browser paints.
 */
function usePrependScrollCompensation({
  rows,
  wrapperRef,
  virtualizer,
  hasMoreBefore,
  enabled,
}: {
  rows: Row<ETableRow>[];
  wrapperRef: RefObject<HTMLDivElement | null>;
  virtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
  hasMoreBefore?: boolean;
  enabled: boolean;
}): void {
  const previousRowsRef = useRef<{
    ids: string[];
    sizes: Map<string, number>;
    hasMoreBefore: boolean;
  }>(undefined);

  useLayoutEffect(() => {
    const previous = previousRowsRef.current;
    const scrollElement = wrapperRef.current;

    /** Measured height of a row, or its estimate when not measured yet. */
    function getRowSize(rowId: string, index: number): number {
      return (
        virtualizer.itemSizeCache.get(rowId) ??
        virtualizer.options.estimateSize(index)
      );
    }

    previousRowsRef.current = {
      ids: rows.map((row) => row.id),
      sizes: new Map(
        rows.map((row, index) => [row.id, getRowSize(row.id, index)])
      ),
      hasMoreBefore: Boolean(hasMoreBefore),
    };

    if (!enabled || !previous || !scrollElement || rows.length === 0) {
      return;
    }

    let delta = getLeadingRowsDelta(rows, previous, getRowSize);
    if (previous.hasMoreBefore && !hasMoreBefore) {
      // the top load trigger row left the DOM together with this data change
      delta -= LOAD_TRIGGER_HEIGHT_IN_REM * getRemInPx();
    }
    if (delta !== 0) {
      scrollElement.scrollTop += delta;
    }
  }, [rows, hasMoreBefore, enabled, virtualizer, wrapperRef]);
}

/**
 * Height (in px) of the rows added to or removed from the start of the data
 * window, 0 when the change was a reorder (e.g. a changed sort) rather than
 * a prepend or trim.
 */
function getLeadingRowsDelta(
  rows: Row<ETableRow>[],
  previous: {ids: string[]; sizes: Map<string, number>},
  getRowSize: (rowId: string, index: number) => number
): number {
  const firstRowId = rows[0]!.id;
  const [previousFirstRowId] = previous.ids;
  if (previousFirstRowId === undefined || previousFirstRowId === firstRowId) {
    return 0;
  }

  const indexOfPreviousFirstRow = rows.findIndex(
    (row) => row.id === previousFirstRowId
  );
  if (indexOfPreviousFirstRow > 0) {
    // rows were prepended — unless the leading rows existed before, which
    // means the rows were reordered
    const previousIds = new Set(previous.ids);
    const prependedRows = rows.slice(0, indexOfPreviousFirstRow);
    if (prependedRows.some((row) => previousIds.has(row.id))) {
      return 0;
    }
    return prependedRows.reduce(
      (delta, row, index) => delta + getRowSize(row.id, index),
      0
    );
  }

  if (indexOfPreviousFirstRow === -1) {
    // leading rows were trimmed — unless they still exist further down,
    // i.e. the rows were reordered
    const indexOfNewFirstRowBefore = previous.ids.indexOf(firstRowId);
    if (indexOfNewFirstRowBefore > 0) {
      const currentIds = new Set(rows.map((row) => row.id));
      const trimmedIds = previous.ids.slice(0, indexOfNewFirstRowBefore);
      if (trimmedIds.every((id) => !currentIds.has(id))) {
        return -trimmedIds.reduce(
          (delta, id) => delta + (previous.sizes.get(id) ?? 0),
          0
        );
      }
    }
  }

  return 0;
}

function getRemInPx(): number {
  const fontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
}
